/**
 * SSAReportGenerator Component
 * 
 * UI for generating PSSA/SSA reports from work products.
 */

import React, { useState } from 'react';
import { 
  FileText, 
  Download, 
  CheckSquare, 
  Square,
  Loader2,
  FileCheck 
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Label } from '@/components/ui/Label';
import { safetyApi } from '@/services/safetyApi';
import type { SafetyAssessment } from '@aerotage/shared';

interface SSAReportGeneratorProps {
  assessment: SafetyAssessment;
}

type ReportSection = 'sfha' | 'fta' | 'fmea' | 'cma' | 'traceability';

export const SSAReportGenerator: React.FC<SSAReportGeneratorProps> = ({ assessment }) => {
  const [reportType, setReportType] = useState<'pssa_report' | 'ssa_report'>(
    assessment.stage === 'ssa' ? 'ssa_report' : 'pssa_report'
  );
  const [selectedSections, setSelectedSections] = useState<ReportSection[]>([
    'sfha', 'fta', 'fmea', 'cma', 'traceability'
  ]);
  const [format, setFormat] = useState<'document' | 'pdf'>('document');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedReport, setGeneratedReport] = useState<{ documentNumber: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sections: { id: ReportSection; label: string; description: string }[] = [
    { id: 'sfha', label: 'SFHA', description: 'System Functional Hazard Assessment' },
    { id: 'fta', label: 'FTA', description: 'Fault Tree Analysis' },
    { id: 'fmea', label: 'FMEA', description: 'Failure Modes and Effects Analysis' },
    { id: 'cma', label: 'CMA', description: 'Common Mode Analysis' },
    { id: 'traceability', label: 'Traceability', description: 'Traceability Matrix' },
  ];

  const toggleSection = (sectionId: ReportSection) => {
    setSelectedSections((prev) =>
      prev.includes(sectionId)
        ? prev.filter((s) => s !== sectionId)
        : [...prev, sectionId]
    );
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);
    setGeneratedReport(null);

    try {
      const result = await safetyApi.generateReport(assessment.assessmentId, {
        reportType,
        includeSections: selectedSections,
        format,
      });
      setGeneratedReport(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate report');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6">
      <div className="bg-card border border-border rounded-lg p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <FileText className="w-8 h-8 text-primary" />
          <div>
            <h2 className="text-xl font-semibold text-foreground">Generate SSA Report</h2>
            <p className="text-sm text-muted-foreground">
              Create a comprehensive safety assessment report from your work products.
            </p>
          </div>
        </div>

        {/* Report Type */}
        <div className="mb-6">
          <Label className="block text-sm font-medium text-muted-foreground mb-2">
            Report Type
          </Label>
          <div className="flex gap-4">
            <Label className="flex items-center gap-2 cursor-pointer">
              {/* eslint-disable-next-line no-restricted-syntax */}
              <input
                type="radio"
                name="reportType"
                value="pssa_report"
                checked={reportType === 'pssa_report'}
                onChange={() => setReportType('pssa_report')}
                className="w-4 h-4 text-primary"
              />
              <span className="text-foreground">PSSA Report</span>
              <span className="text-sm text-muted-foreground">(Preliminary)</span>
            </Label>
            <Label className="flex items-center gap-2 cursor-pointer">
              {/* eslint-disable-next-line no-restricted-syntax */}
              <input
                type="radio"
                name="reportType"
                value="ssa_report"
                checked={reportType === 'ssa_report'}
                onChange={() => setReportType('ssa_report')}
                className="w-4 h-4 text-primary"
              />
              <span className="text-foreground">SSA Report</span>
              <span className="text-sm text-muted-foreground">(Final)</span>
            </Label>
          </div>
        </div>

        {/* Sections */}
        <div className="mb-6">
          <Label className="block text-sm font-medium text-muted-foreground mb-2">
            Include Sections
          </Label>
          <div className="space-y-2">
            {sections.map((section) => {
              const isSelected = selectedSections.includes(section.id);
              return (
                <Button
                  key={section.id}
                  variant="ghost"
                  onClick={() => toggleSection(section.id)}
                  className={`w-full flex items-center gap-3 p-3 h-auto justify-start rounded-lg border transition-colors ${
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : 'border-border bg-background hover:border-border'
                  }`}
                >
                  {isSelected ? (
                    <CheckSquare className="w-5 h-5 text-primary" />
                  ) : (
                    <Square className="w-5 h-5 text-muted-foreground" />
                  )}
                  <div className="text-left">
                    <span className="font-medium text-foreground">{section.label}</span>
                    <span className="text-sm text-muted-foreground ml-2">
                      - {section.description}
                    </span>
                  </div>
                </Button>
              );
            })}
          </div>
        </div>

        {/* Format */}
        <div className="mb-6">
          <Label className="block text-sm font-medium text-muted-foreground mb-2">
            Output Format
          </Label>
          <div className="flex gap-4">
            <Label className="flex items-center gap-2 cursor-pointer">
              {/* eslint-disable-next-line no-restricted-syntax */}
              <input
                type="radio"
                name="format"
                value="document"
                checked={format === 'document'}
                onChange={() => setFormat('document')}
                className="w-4 h-4 text-primary"
              />
              <span className="text-foreground">Aerotage Document</span>
            </Label>
            <Label className="flex items-center gap-2 cursor-pointer">
              {/* eslint-disable-next-line no-restricted-syntax */}
              <input
                type="radio"
                name="format"
                value="pdf"
                checked={format === 'pdf'}
                onChange={() => setFormat('pdf')}
                className="w-4 h-4 text-primary"
              />
              <span className="text-foreground">PDF Export</span>
            </Label>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-3 bg-destructive/10 border border-destructive/30 rounded-lg text-destructive">
            {error}
          </div>
        )}

        {/* Success */}
        {generatedReport && (
          <div className="mb-6 p-4 bg-success/10 border border-success/30 rounded-lg">
            <div className="flex items-center gap-3">
              <FileCheck className="w-6 h-6 text-success" />
              <div>
                <p className="font-medium text-success">Report Generated Successfully</p>
                <p className="text-sm text-muted-foreground">
                  Document Number: {generatedReport.documentNumber}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button
            onClick={handleGenerate}
            disabled={isGenerating || selectedSections.length === 0}
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Generate Report
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Previous Reports */}
      {assessment.reports.length > 0 && (
        <div className="mt-6 bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-medium text-foreground mb-4">Previous Reports</h3>
          <div className="space-y-2">
            {assessment.reports.map((report, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-background rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium text-foreground">{report.documentNumber}</p>
                    <p className="text-sm text-muted-foreground">
                      {report.reportType === 'pssa_report' ? 'PSSA' : 'SSA'} • 
                      v{report.version} • 
                      {new Date(report.generatedAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <span className={`px-2 py-1 text-xs rounded ${
                  report.status === 'final' 
                    ? 'bg-success/20 text-success' 
                    : 'bg-muted/20 text-muted-foreground'
                }`}>
                  {report.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
